import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { SUPPORT_HUB_NAME } from '../../config/constants';

const {
  FiMail,
  FiLock,
  FiLogIn,
  FiEye,
  FiEyeOff,
  FiShield,
  FiInbox,
  FiZap,
  FiCheckCircle,
} = FiIcons;

const LoginScreen = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(email, password);
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-shell min-h-screen flex items-center justify-center p-4 md:p-8">
      <motion.main
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="w-full max-w-6xl rounded-3xl overflow-hidden border border-border shadow-2xl bg-surface grid lg:grid-cols-[1.05fr_0.95fr]"
      >
        <section className="login-brand-panel relative min-h-[620px] p-8 md:p-10 lg:p-12 flex flex-col justify-between overflow-hidden">
          <div className="login-pattern" aria-hidden="true" />
          <div className="login-glow login-glow-one" aria-hidden="true" />
          <div className="login-glow login-glow-two" aria-hidden="true" />

          <div className="relative z-10">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-primary flex items-center justify-center shadow-lg">
                <SafeIcon icon={FiShield} className="w-6 h-6 text-surface" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] login-brand-muted">BPA Solutions</p>
                <p className="text-lg font-bold text-surface">Support Hub</p>
              </div>
            </div>

            <div className="mt-14 max-w-xl">
              <div className="inline-flex items-center gap-2 rounded-full login-chip px-3 py-1.5 text-xs font-semibold">
                <span className="w-2 h-2 rounded-full bg-accent" />
                Central support operations
              </div>
              <h1 className="mt-5 text-4xl md:text-5xl font-bold leading-tight text-surface">
                One calm place for every customer issue.
              </h1>
              <p className="mt-5 text-base md:text-lg leading-7 login-brand-muted max-w-lg">
                Triage, assign, resolve and track support requests across every BPA Solutions client application from one secure workspace.
              </p>
            </div>

            <div className="mt-10 grid sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3 gap-3">
              <Feature icon={FiInbox} title="Central inbox" text="Every app, one queue" />
              <Feature icon={FiZap} title="Fast triage" text="Prioritise instantly" />
              <Feature icon={FiCheckCircle} title="Auditable" text="Full ticket history" />
            </div>
          </div>

          <div className="relative z-10 flex items-center justify-between gap-4 pt-10">
            <p className="text-xs login-brand-muted">Secure access for authorised BPA Solutions support staff.</p>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-surface whitespace-nowrap">
              <SafeIcon icon={FiShield} className="w-3.5 h-3.5 text-accent" />
              Protected workspace
            </span>
          </div>
        </section>

        <section className="p-7 sm:p-10 lg:p-12 flex items-center bg-surface">
          <div className="w-full max-w-md mx-auto">
            <div className="mb-8">
              <p className="text-sm font-semibold text-primary">Welcome back</p>
              <h2 className="mt-1 text-3xl font-bold text-text">Sign in to {SUPPORT_HUB_NAME}</h2>
              <p className="mt-2 text-sm text-muted">Use your Support Hub account to continue.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <label className="block">
                <span className="block text-sm font-semibold text-text mb-2">Email address</span>
                <div className="relative">
                  <SafeIcon icon={FiMail} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted w-5 h-5" />
                  <input
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    className="control pl-11 py-3"
                    placeholder="name@bpasolutions.au"
                  />
                </div>
              </label>

              <label className="block">
                <span className="block text-sm font-semibold text-text mb-2">Password</span>
                <div className="relative">
                  <SafeIcon icon={FiLock} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted w-5 h-5" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className="control pl-11 pr-12 py-3"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((current) => !current)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-2 rounded-lg text-muted hover:text-text hover:bg-background"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="w-4 h-4" />
                  </button>
                </div>
              </label>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-xl bg-primary-soft border border-border px-4 py-3 text-sm text-text"
                >
                  <span className="font-semibold text-primary">Unable to sign in.</span> {error}
                </motion.div>
              )}

              <motion.button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-primary text-surface py-3.5 rounded-xl font-semibold hover:bg-primary-hover focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md"
                whileTap={{ scale: loading ? 1 : 0.99 }}
              >
                <SafeIcon icon={FiLogIn} className="w-4 h-4" />
                {loading ? 'Signing in...' : 'Sign in securely'}
              </motion.button>
            </form>

            <div className="mt-8 pt-6 border-t border-border flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-accent-soft flex items-center justify-center shrink-0">
                <SafeIcon icon={FiShield} className="w-4 h-4 text-accent" />
              </div>
              <p className="text-xs leading-5 text-muted">
                Your credentials are authenticated against the Support Hub backend. This screen does not store your password in the browser.
              </p>
            </div>
          </div>
        </section>
      </motion.main>
    </div>
  );
};

const Feature = ({ icon, title, text }) => (
  <div className="login-feature rounded-2xl p-4">
    <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center mb-3">
      <SafeIcon icon={icon} className="w-4 h-4 text-surface" />
    </div>
    <p className="text-sm font-semibold text-surface">{title}</p>
    <p className="text-xs login-brand-muted mt-1">{text}</p>
  </div>
);

export default LoginScreen;